import { useMemo } from 'react'

import { useFormContext } from '@circlefin/form'

import { useL3Onboarding } from '../l3-onboarding'

import type { BusinessRegistrationValues } from '../../containers/BusinessDetails/BusinessRegistration/BusinessRegistration.schema'
import type { PhysicalBusinessAddressValues } from '../../containers/BusinessDetails/PhysicalBusinessAddress/PhysicalBusinessAddress.schema'
import type { Jurisdiction, L3OnboardingFieldName } from '@shared/graphql'

interface CountryIncludesProps {
  /**
   * Country includes one of the CountryCodes in the list.
   */
  countryIncludes?: Array<Jurisdiction>
  countryExcludes?: never
}

interface CountryExcludesNotProps {
  countryIncludes?: never
  /**
   * Country excludes one of the CountryCodes in the list.
   */
  countryExcludes?: Array<Jurisdiction>
}

interface NoneProps {
  countryIncludes?: never
  countryExcludes?: never
}

export type FieldValues = Pick<
  BusinessRegistrationValues,
  `${L3OnboardingFieldName.countryOfFormation}`
> &
  Pick<
    PhysicalBusinessAddressValues,
    `${L3OnboardingFieldName.physicalAddressCountry}`
  >

export type UseCountryRestrictedProps =
  | CountryIncludesProps
  | CountryExcludesNotProps
  | NoneProps

export const useCountryRestricted = ({
  countryIncludes,
  countryExcludes,
}: UseCountryRestrictedProps = {}) => {
  const [, { getApplicableJurisdiction }] = useL3Onboarding()
  const { watch } = useFormContext<FieldValues>()

  const [formCountryOfFormation, formPhysicalAddressCountry] = watch([
    'countryOfFormation',
    'physicalAddressCountry',
  ])

  return useMemo(() => {
    if (countryIncludes == null && countryExcludes == null) {
      return false
    }

    const applicableJurisdiction = getApplicableJurisdiction({
      formCountryOfFormation,
      formPhysicalAddressCountry,
    })

    /**
     * Only applicable for countries specified within `countryIncludes`.
     *
     * Return false if not restricted.
     */
    if (countryIncludes != null && applicableJurisdiction != null) {
      return !countryIncludes.includes(applicableJurisdiction)
    }

    /**
     * Only applicable for countries specified within `countryExcludes`.
     *
     * Return true if restricted.
     */
    if (countryExcludes != null && applicableJurisdiction != null) {
      return countryExcludes.includes(applicableJurisdiction)
    }
  }, [
    getApplicableJurisdiction,
    formCountryOfFormation,
    formPhysicalAddressCountry,
    countryIncludes,
    countryExcludes,
  ])
}
